import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EmployeesService } from '../../employees.service';
import { finalize, Subscription } from 'rxjs';
import { Designation, Employee } from '../../employees.model';
import { Action, PaginatedResponse } from '../../../common/models/common.model';
import { DesignationService } from '../../designation.service';
import { Role } from '../../../common/models/role.model';
import { RoleService } from '../../../services/role.service';

@Component({
  selector: 'app-employee-info-form',
  standalone: false,
  templateUrl: './employee-info-form.component.html',
  styleUrl: './employee-info-form.component.scss',
})
export class EmployeeInfoFormComponent implements OnInit, OnDestroy {
  @Input() employee: Employee | null = null;
  @Input() mode: Action = Action.CREATE;
  @Output() onCancelEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSaveEvent: EventEmitter<Employee> = new EventEmitter<Employee>();

  protected employeeForm!: FormGroup;
  protected showPassword: boolean = false;
  protected isSavingEmployeeInfo: boolean = false;
  protected designations: Designation[] = [];
  protected isDesignationListLoading: boolean = true;
  protected previewProfilePicture: string | null = null;
  protected initialEmployeeFormValue: any = null;

  protected apiError: any = null;
  private _selectedProfilePictureFile: File | null = null;
  private _employeeSubscription!: Subscription;
  private _designationSubscription!: Subscription;
  private _roleSubscription!: Subscription;

  protected readonly Action = Action;

  protected roleOptions: Role[] = [];

  constructor(
    private _employeeService: EmployeesService,
    private _designationService: DesignationService,
    private _roleService: RoleService
  ) {}

  ngOnInit(): void {
    this._fetchDesignationList();
    this._fetchRoleList();
  }

  // Protected methods

  protected onSubmitEmployeeForm(): void {
    if (this.employeeForm.invalid) return;

    this.isSavingEmployeeInfo = true;
    this.apiError = null;

    const formData = new FormData();
    const currentValues = this.employeeForm.getRawValue();

    // Only append changed fields in EDIT mode
    const shouldAppendField = (field: string): boolean => {
      return (
        this.mode === Action.CREATE ||
        ((this.employeeForm.get(field)?.dirty ?? false) &&
          currentValues[field] !== this.initialEmployeeFormValue?.[field])
      );
    };

    const userFields = [
      'email',
      'first_name',
      'last_name',
      'phone_number',
      'role',
      'address_line1',
      'address_line2',
      'city',
      'postal_code',
      'country',
      'aadhar_card_number',
      'date_of_birth',
    ];

    // Append user fields
    userFields.forEach(field => {
      if (shouldAppendField(field)) {
        formData.append(`user.${field}`, currentValues[field] ?? '');
      }
    });

    // Password (CREATE only)
    if (this.mode === Action.CREATE) {
      formData.append('user.password', currentValues['password'] || '');
    }

    // Designation and DOJ
    if (shouldAppendField('designation')) {
      formData.append('designation', currentValues['designation'] || null);
    }

    if (shouldAppendField('date_of_joining')) {
      formData.append('date_of_joining', currentValues['date_of_joining'] || '');
    }

    // Profile picture (optional)
    if (this._selectedProfilePictureFile) {
      formData.append('user.profile_picture', this._selectedProfilePictureFile);
    }

    const request$ =
      this.mode === Action.EDIT && this.employee
        ? this._employeeService.partial_update(this.employee.id, formData)
        : this._employeeService.post(formData);

    this._employeeSubscription = request$
      .pipe(
        finalize(() => {
          this.isSavingEmployeeInfo = false;
        })
      )
      .subscribe({
        next: (employeeInfo: Employee) => this.onSaveEvent.emit(employeeInfo),
        error: error => {
          this.apiError = error;
        },
      });
  }

  protected onProfilePictureSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this._selectedProfilePictureFile = input.files[0];

      const reader = new FileReader();
      reader.onload = () => {
        this.previewProfilePicture = reader.result as string;
      };
      reader.readAsDataURL(this._selectedProfilePictureFile);
    }
  }

  // Private methods

  private _fetchDesignationList(): void {
    this.isDesignationListLoading = true;

    this._designationSubscription = this._designationService
      .list()
      .pipe(finalize(() => (this.isDesignationListLoading = false)))
      .subscribe({
        next: (designationList: Designation[]) => {
          this.designations = designationList;
        },
        error: err => {
          console.error(err);
        },
        complete: () => {
          this._initializeEmployeeForm();
        },
      });
  }

  private _fetchRoleList(): void {
    this._roleSubscription = this._roleService.list().subscribe({
      next: (roles: Role[]) => {
        // Only active roles can be assigned. In EDIT mode the employee's own role is kept even
        // once retired, otherwise their role would render blank in the picker.
        const currentRole = this.mode === Action.EDIT ? this.employee?.user?.role : null;
        this.roleOptions = roles.filter(role => role.is_active || role.code === currentRole);
      },
      error: err => {
        console.error(err);
      },
    });
  }

  private _initializeEmployeeForm(): void {
    this.employeeForm = new FormGroup({
      email: new FormControl(this.employee?.user?.email || '', [Validators.required, Validators.email]),
      password: new FormControl('', this.mode === Action.CREATE ? Validators.required : []),
      first_name: new FormControl(this.employee?.user?.first_name || '', Validators.required),
      last_name: new FormControl(this.employee?.user?.last_name || '', Validators.required),
      phone_number: new FormControl(this.employee?.user?.phone_number || '', Validators.required),
      role: new FormControl(this.employee?.user?.role || 'employee', Validators.required),
      designation: new FormControl(this.employee?.designation?.id || null, Validators.required),
      address_line1: new FormControl(this.employee?.user?.address_line1 || '', Validators.required),
      address_line2: new FormControl(this.employee?.user?.address_line2 || ''),
      postal_code: new FormControl(this.employee?.user?.postal_code || '', Validators.required),
      city: new FormControl(this.employee?.user?.city || '', Validators.required),
      country: new FormControl({ value: this.employee?.user?.country || 'India', disabled: true }, Validators.required),
      date_of_birth: new FormControl(this.employee?.user?.date_of_birth || '', Validators.required),
      date_of_joining: new FormControl(this.employee?.date_of_joining || '', Validators.required),
      aadhar_card_number: new FormControl(this.employee?.user?.aadhar_card_number || '', [
        Validators.required,
        Validators.pattern('^[0-9]{12}$'),
      ]),
    });

    this.previewProfilePicture = this.employee?.user?.profile_picture ?? null;
    this.initialEmployeeFormValue = this.employeeForm.getRawValue();
  }

  ngOnDestroy(): void {
    this._employeeSubscription?.unsubscribe();
    this._designationSubscription?.unsubscribe();
    this._roleSubscription?.unsubscribe();
  }
}
